'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useCookieConsent } from '../../context/CookieConsentContext';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { theme } from '../../theme';

type CategoryKey = 'analytics' | 'marketing';

export default function CookieConsent() {
  const { status, categories, acceptAll, rejectAll, savePreferences } = useCookieConsent();
  const t = useTranslations('cookies');
  const { localizeFullPath } = useLocalizedPath();
  const [mounted, setMounted] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [draft, setDraft] = useState<{ analytics: boolean; marketing: boolean }>({
    analytics: categories.analytics,
    marketing: categories.marketing,
  });

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    setDraft({ analytics: categories.analytics, marketing: categories.marketing });
  }, [categories.analytics, categories.marketing]);

  if (!mounted || status !== 'pending') return null;

  const toggleDraft = (key: CategoryKey) => {
    setDraft((d) => ({ ...d, [key]: !d[key] }));
  };

  const secondaryButtonStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: theme.borderRadius.lg,
    border: `1px solid ${theme.colors.border.dark}`,
    background: theme.colors.background.primary,
    color: theme.colors.text.primary,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.medium,
    cursor: 'pointer',
    transition: theme.transitions.fast,
  };

  const primaryButtonStyle: React.CSSProperties = {
    padding: '10px 20px',
    borderRadius: theme.borderRadius.lg,
    border: 'none',
    background: 'var(--color-primary-solid)',
    color: 'var(--color-on-brand)',
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.semibold,
    cursor: 'pointer',
    transition: theme.transitions.fast,
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: theme.zIndex.toast ?? 1080,
      background: theme.colors.background.primary,
      borderTop: `1px solid ${theme.colors.border.light}`,
      boxShadow: theme.shadows.xl,
      padding: '20px 24px',
      fontFamily: theme.fonts.body,
      animation: 'slideUp 0.3s ease',
    }}>
      <div style={{
        display: 'flex',
        alignItems: customizing ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}>
        <p style={{
          margin: 0,
          fontSize: theme.fontSizes.sm,
          color: theme.colors.text.secondary,
          lineHeight: 1.5,
          flex: '1 1 400px',
        }}>
          {t('message')}{' '}
          <Link href={localizeFullPath('/cookie-policy')} style={{ color: 'var(--color-link)', textDecoration: 'underline' }}>
            {t('learnMore')}
          </Link>
        </p>

        {!customizing && (
          <div style={{ display: 'flex', gap: '12px', flexShrink: 0, flexWrap: 'wrap' }}>
            <button type="button" onClick={rejectAll} style={secondaryButtonStyle}>
              {t('rejectAll')}
            </button>
            <button type="button" onClick={() => setCustomizing(true)} style={secondaryButtonStyle}>
              {t('customize')}
            </button>
            <button type="button" onClick={acceptAll} style={primaryButtonStyle}>
              {t('acceptAll')}
            </button>
          </div>
        )}
      </div>

      {customizing && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <CategoryRow
            title={t('categoryNecessary')}
            description={t('categoryNecessaryDesc')}
            checked
            disabled
            onChange={() => {}}
          />
          <CategoryRow
            title={t('categoryAnalytics')}
            description={t('categoryAnalyticsDesc')}
            checked={draft.analytics}
            onChange={() => toggleDraft('analytics')}
          />
          <CategoryRow
            title={t('categoryMarketing')}
            description={t('categoryMarketingDesc')}
            checked={draft.marketing}
            onChange={() => toggleDraft('marketing')}
          />
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 4 }}>
            <button type="button" onClick={rejectAll} style={secondaryButtonStyle}>
              {t('rejectAll')}
            </button>
            <button type="button" onClick={() => savePreferences(draft)} style={secondaryButtonStyle}>
              {t('save')}
            </button>
            <button type="button" onClick={acceptAll} style={primaryButtonStyle}>
              {t('acceptAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface CategoryRowProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}

function CategoryRow({ title, description, checked, disabled, onChange }: CategoryRowProps) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
      padding: '10px 12px',
      border: `1px solid ${theme.colors.border.light}`,
      borderRadius: theme.borderRadius.md,
      background: theme.colors.background.secondary,
    }}>
      <div style={{ flex: '1 1 auto' }}>
        <div style={{
          fontSize: theme.fontSizes.sm,
          fontWeight: theme.fontWeights.semibold,
          color: theme.colors.text.primary,
        }}>
          {title}
        </div>
        <div style={{
          fontSize: theme.fontSizes.xs,
          color: theme.colors.text.secondary,
          marginTop: 4,
          lineHeight: 1.4,
        }}>
          {description}
        </div>
      </div>
      <label style={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          aria-label={title}
          style={{ width: 18, height: 18, cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      </label>
    </div>
  );
}
